import React from 'react';
import styled from 'styled-components';
import { Box, Flex } from '@dutchie/common/components/rebass-compat';

import { RewardsRibbon, RibbonColorPallettes } from 'shared/assets/rewards-ribbon';
import { CheckoutText } from 'checkout/components/styles/common';
import { Button, CheckoutAccordionSummary } from 'shared/components';
import { mediaQueries } from 'shared/styles';
import useTranslation from 'hooks/use-translation';
import useStores from 'shared/hooks/use-stores';
import { getAiqSmsPinTriggerDecision } from 'shared/helpers/rewards';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useObserver } from 'mobx-react-lite';

export function RewardsConnectReadySummary({
  onClick,
  onResendPin,
  rewardsProgramDisplayName,
  rewardsProgramDisplayDescription,
}) {
  const { t } = useTranslation();
  const { Cart, User } = useStores();
  const flags = useFlags();
  const isAiqSmsPinTriggerSettingEnabled = flags['pos.customers.aiq-sms-pin-trigger-setting.rollout'] ?? false;
  const isSendSMSEnabled = flags['pos.customers.skip-sms-sending.rollout'] ?? false;
  const isAlpineIQEnabled = Cart.order.dispensary?.consumerDispensaryIntegrations?.alpineiq?.enabled ?? false;
  const { rewardsType } = Cart;
  const isNonAiqRewardsProgram = !!rewardsType && rewardsType !== 'Alpineiq';
  const isAiqRewardsProgram = rewardsType === 'Alpineiq' || (!rewardsType && isAlpineIQEnabled);
  const { shouldDeferBlankPinOperation, shouldSkipSmsPin } = useObserver(() =>
    getAiqSmsPinTriggerDecision({
      isAiqSmsPinTriggerSettingEnabled,
      legacyShouldSkipSmsPin: isSendSMSEnabled,
      dispensaryId: Cart.order.dispensary?.id,
      userId: User?.id,
      readiness: Cart.aiqSmsPinTriggerSettingsReadiness,
      aiqSmsPinTriggerBehavior: Cart.order.dispensary?.storeSettings?.aiqSmsPinTriggerBehavior,
    })
  );
  const shouldDeferConnect =
    isAiqSmsPinTriggerSettingEnabled && !isNonAiqRewardsProgram && (!isAlpineIQEnabled || shouldDeferBlankPinOperation);
  const shouldResendSmsPin = isAiqRewardsProgram ? shouldSkipSmsPin : isSendSMSEnabled;

  const handleConnect = async () => {
    await onResendPin();
    onClick();
  };

  return (
    <StyledCheckoutAccordionSummary>
      <StyledFlex alignItems='center' justify='center'>
        <RewardsRibbon colorPalette={RibbonColorPallettes.blue} />
        <StyledBox>
          <HeaderText>
            {t('rewards.connectToDisplayName', {
              defaultValue: 'Connect to {{rewardsProgramDisplayName}}',
              rewardsProgramDisplayName,
            })}
          </HeaderText>
          <BodyText>
            {t('rewards.connectToDisplayName', {
              defaultValue: '{{rewardsProgramDisplayDescription}}',
              rewardsProgramDisplayDescription,
            })}
          </BodyText>
        </StyledBox>
        <StyledButton disabled={shouldDeferConnect} onClick={shouldResendSmsPin ? handleConnect : onClick}>
          {t('rewards.connect', 'CONNECT')}
        </StyledButton>
      </StyledFlex>
    </StyledCheckoutAccordionSummary>
  );
}

const StyledBox = styled(Box)`
  padding-left: 14.75px;

  @media ${mediaQueries.largePhone} {
    padding-left: 0;
  }
`;

const BodyText = styled(CheckoutText)`
  padding-bottom: 20px;
  line-height: 16px;
  font-size: 12px;
  max-width: 200px;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.grey[35]};

  @media ${mediaQueries.largePhone} {
    padding-bottom: 10px;
  }
`;

const HeaderText = styled(CheckoutText)`
  font-weight: bold;
  font-size: 13px;
  line-height: 113%;
  padding-top: 21px;
  max-width: 200px;
  color: ${({ theme }) => theme.colors.grey[35]};

  @media ${mediaQueries.largePhone} {
    padding-top: 15px;
    padding-bottom: 11px;
    line-height: 13px;
    max-width: unset;
  }
`;

const StyledButton = styled(Button)`
  width: 77px !important;
  height: 27.13px;
  font-size: 12px;
  line-height: 14px;
  padding: 0 10px;
  right: 0;
  margin: 0 auto;
  margin-left: 10px;

  @media ${mediaQueries.largePhone} {
    margin-left: auto;
  }
`;

const StyledCheckoutAccordionSummary = styled(CheckoutAccordionSummary)`
  &.MuiAccordionSummary-root {
    padding: 0 17px;
    height: 92px;
  }

  @media ${mediaQueries.largePhone} {
    display: block !important;
  }
`;

const StyledFlex = styled(Flex)`
  svg {
    width: 52.32px;
    height: 52.32px;
  }

  @media ${mediaQueries.largePhone} {
    display: block;
    text-align: center;
    padding-top: 15px;

    svg {
      width: 66.64px;
      height: 66.64px;
    }

    @media ${mediaQueries.largePhone} {
      svg {
        width: 52.32px;
        height: 52.32px;
      }
    }
  }
`;
